import axios from '@/app/axios'
import bus from '@/app/bus'
import routing from '@/app/config/routing'
import router from '@/app/router'
import store from '@/app/store'
import Responses from '@/app/utils/responses'
import AuthRequest from '@/users/requests/auth-request'
import AuthResponse from '@/users/responses/auth-response'
import AuthModule from '@/users/store/auth-module'
import { getModule } from 'vuex-module-decorators'
import { AxiosResponse } from 'axios'
import ServiceResult from '@/common/models/service-result'
import AuthRequestTwoFactor from '../requests/auth-request-two-factor'
import AccountModule from '../store/account-module'

class AuthService {
  public async loginAsync(request: AuthRequest) {
    const res = await axios.post<AuthResponse>('auth/login', request)
    return this.handleAuthResponse(res)
  }

  public async loginTwoFactorAsync(request: AuthRequestTwoFactor) {
    const res = await axios.post<AuthResponse>('auth/loginTwoFactor', request)
    return this.handleAuthResponse(res)
  }

  public async impersonate(request: AuthRequest) {
    const res = await axios.post<AuthResponse>('auth/impersonate', request)
    return this.handleAuthResponse(res)
  }

  public async resetPasswordAsync(
    email: string,
    token: string,
    password: string
  ) {
    const res = await axios.post<ServiceResult>('auth/password/reset', {
      email,
      token,
      password,
    })
    return res.data
  }

  public async sendPasswordResetLinkAsync(email: string) {
    const res = await axios.post<ServiceResult>('auth/sendPasswordResetLink', {
      email,
    })
    return res.data
  }

  public async changePasswordAsync(
    password: string,
    passwordConfirmation: string
  ) {
    const res = await axios.post<ServiceResult>('auth/password/change', {
      password,
      passwordConfirmation,
    })
    return res.data
  }

  public async logoutAsync() {
    const authModule = getModule(AuthModule, store)
    await axios.post('auth/logout')
    authModule.logout()

    const accountModule = getModule(AccountModule, store)
    accountModule.logout()

    // Browser History cannot be flushed.
    router.push(routing.logoutRedirect)
  }

  public authenticated() {
    const authModule = getModule(AuthModule, store)
    return authModule.authenticated
  }

  public isInRole(role: string) {
    const authModule = getModule(AuthModule, store)
    return authModule.roles.some((r) => r === role)
  }

  public isInAnyRole(roles: string[]) {
    return roles.some(this.isInRole)
  }

  public user() {
    const authModule = getModule(AuthModule, store)
    return authModule.user
  }

  public tenant() {
    const authModule = getModule(AuthModule, store)
    return authModule.tenant
  }

  public displayName() {
    const authModule = getModule(AuthModule, store)
    return authModule.displayName
  }

  private redirect() {
    // Check for pending redirect first
    const pendingRedirect = localStorage.getItem('pendingRedirect')
    if (pendingRedirect) {
      localStorage.removeItem('pendingRedirect')
      router.push(pendingRedirect)
      return
    }
    // Get redirect URL from query parameter
    const redirectUrl = router.currentRoute.query.redirect as string

    if (redirectUrl) {
      router.push(redirectUrl)
      return
    }

    // Original fallback logic
    const authModule = getModule(AuthModule, store)
    const role = authModule.roles.length > 0 ? authModule.roles[0] : 'DEFAULT'

    const routes = routing.loginDefaultRedirect as { [s: string]: string }
    if (routes[role]) {
      router.push(routes[role])
    } else {
      router.push(routes['DEFAULT'])
    }
  }

  private handleAuthResponse(res: AxiosResponse<AuthResponse>) {
    const authModule = getModule(AuthModule, store)
    const response = Responses.obj(res)
    if (response != null) {
      if (response.authenticated && response.token !== null) {
        authModule.login(response)
        bus.$emit('auth.logged-in')
        this.redirect()
      } else if (response.twoFactorEnabled) {
        router.push(`/login-two-factor/${response.email}`)
      }
      return response
    }
    return null
  }
}

export default new AuthService()
